import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Search,
  RotateCcw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Inbox as InboxIcon,
  ExternalLink,
  AlertCircle,
  Info,
  Plus,
  X,
} from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";
import EmptyState from "@/components/journey/EmptyState";

const KIND_OPTIONS = [
  { value: "", label: "Alle Typen" },
  { value: "standort", label: "Standortlead" },
  { value: "gruender", label: "Gründerlead" },
  { value: "kontakt", label: "Kontaktanfrage" },
  { value: "dev_program", label: "Dev-Programm" },
  { value: "sales_partner", label: "Sales Partner" },
];

const STAGE_OPTIONS = [
  { value: "", label: "Alle Status" },
  { value: "new", label: "Neu" },
  { value: "appointment_pending", label: "Erstgespräch ausstehend" },
  { value: "appointment_booked", label: "Erstgespräch gebucht" },
  { value: "qualification_running", label: "Qualifizierung" },
  { value: "site_check_running", label: "Standortprüfung" },
  { value: "opportunity", label: "Opportunity" },
  { value: "not_a_fit", label: "Nicht passend" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "Alle Quellen" },
  { value: "website", label: "Website" },
  { value: "manual", label: "Manuell" },
  { value: "phone", label: "Telefon" },
  { value: "trade_fair", label: "Messe" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "email", label: "E-Mail" },
  { value: "referral", label: "Empfehlung" },
  { value: "sales_partner", label: "Sales Partner" },
  { value: "other", label: "Sonstige" },
];

const PAGE_SIZE = 25;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return iso;
  }
}

function useDebounced(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function Badge({ children, tone = "default", testId }) {
  const tones = {
    default: "border-white/10 bg-white/[0.04] text-zinc-300",
    accent: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    warn: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    muted: "border-white/5 bg-transparent text-zinc-500",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function stageTone(stage) {
  if (!stage) return "muted";
  if (["won_project", "opportunity"].includes(stage)) return "accent";
  if (["not_a_fit", "lost", "archived"].includes(stage)) return "muted";
  return "default";
}

function LeadRow({ item }) {
  return (
    <tr
      data-testid={`journey-inbox-row-${item.id}`}
      className="border-b border-white/[0.04] text-sm transition hover:bg-white/[0.02]"
    >
      <td className="px-4 py-3 text-xs text-zinc-500">
        {formatDate(item.created_at)}
      </td>
      <td className="px-4 py-3">
        <div className="font-medium text-zinc-100">
          {item.full_name || "—"}
        </div>
        <div className="text-xs text-zinc-500">{item.email || "—"}</div>
      </td>
      <td className="px-4 py-3 text-xs text-zinc-300">{item.company || "—"}</td>
      <td className="px-4 py-3">
        <Badge>{item.kind_label}</Badge>
      </td>
      <td className="px-4 py-3 text-xs text-zinc-400">
        {item.source_type_label}
        {item.source_page ? (
          <div className="text-[10px] text-zinc-600">{item.source_page}</div>
        ) : null}
      </td>
      <td className="px-4 py-3 text-xs text-zinc-300">
        {item.city_region || item.wish_region || "—"}
      </td>
      <td className="px-4 py-3 text-xs text-zinc-300">
        {item.product_interest || item.system_interests || "—"}
      </td>
      <td className="px-4 py-3">
        <Badge tone={stageTone(item.pipeline_stage)}>
          {item.pipeline_stage_label}
        </Badge>
      </td>
      <td className="px-4 py-3 text-xs text-zinc-400">
        {item.next_step || "—"}
      </td>
      <td className="px-4 py-3 text-xs text-zinc-500">
        {item.auto_reply_status || item.internal_notification_status || "—"}
      </td>
      <td className="px-4 py-3 text-right">
        <Link
          to={`/admin/journey/leads/${item.id}`}
          data-testid={`journey-inbox-open-${item.id}`}
          className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-1 text-xs text-zinc-300 transition hover:border-emerald-500/30 hover:text-emerald-300"
        >
          Details
          <ExternalLink className="h-3 w-3" />
        </Link>
      </td>
    </tr>
  );
}

function LeadCard({ item }) {
  return (
    <Link
      to={`/admin/journey/leads/${item.id}`}
      data-testid={`journey-inbox-card-${item.id}`}
      className="block rounded-md border border-white/[0.06] bg-white/[0.02] p-4 transition hover:border-emerald-500/30"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-zinc-100">
            {item.full_name || "—"}
          </div>
          <div className="text-xs text-zinc-500">{item.email || "—"}</div>
        </div>
        <span className="text-[10px] text-zinc-600">
          {formatDate(item.created_at)}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Badge>{item.kind_label}</Badge>
        <Badge tone={stageTone(item.pipeline_stage)}>
          {item.pipeline_stage_label}
        </Badge>
        <Badge tone="muted">{item.source_type_label}</Badge>
      </div>
      {item.company || item.city_region ? (
        <div className="mt-3 text-xs text-zinc-400">
          {[item.company, item.city_region].filter(Boolean).join(" · ")}
        </div>
      ) : null}
    </Link>
  );
}

export default function InboxPage() {
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState("");
  const [pipelineStage, setPipelineStage] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [needsNextStep, setNeedsNextStep] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const token = readToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));
      if (kind) params.set("kind", kind);
      if (pipelineStage) params.set("pipeline_stage", pipelineStage);
      if (sourceType) params.set("source_type", sourceType);
      if (needsNextStep) params.set("needs_next_step", "true");
      if (search) params.set("search", search);
      const r = await axios.get(`${API}/admin/crm/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000,
      });
      setData(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Unbekannter Fehler");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, kind, pipelineStage, sourceType, needsNextStep, search]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to page 1 whenever a filter or search changes.
  useEffect(() => {
    setPage(1);
  }, [kind, pipelineStage, sourceType, search]);

  const totalPages = data?.total_pages ?? 0;
  const total = data?.total ?? 0;
  const items = data?.items ?? [];

  const filtersActive = useMemo(
    () => Boolean(kind || pipelineStage || sourceType || search),
    [kind, pipelineStage, sourceType, search]
  );

  return (
    <div data-testid="journey-inbox-page" className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-zinc-100">PINE Journey Inbox</h1>
          <p className="text-sm text-zinc-400">
            Alle eingehenden Website-Leads und manuell erfassten Kontakte an einem Ort.
          </p>
        </div>
        <Link
          to="/admin/journey/quick-add"
          data-testid="journey-inbox-quick-add"
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded bg-emerald-500 px-3 py-2 text-xs font-semibold text-zinc-950 transition hover:bg-emerald-400"
        >
          <Plus className="h-4 w-4" />
          Lead erfassen
        </Link>
      </header>

      <div className="rounded-md border border-emerald-500/10 bg-emerald-500/[0.03] px-4 py-2 text-xs text-emerald-300/80">
        <Info className="mr-1.5 inline h-3.5 w-3.5" />
        Manuelle Lead-Erfassung ist live — über „Lead erfassen" oder „Vollständig erfassen".
      </div>

      <section className="space-y-3 rounded-md border border-white/[0.06] bg-white/[0.01] p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Suche nach Name, Firma, E-Mail, Region…"
              data-testid="journey-inbox-search"
              className="w-full rounded border border-white/10 bg-[#0B0F10] py-2 pl-9 pr-3 text-sm text-zinc-100 outline-none transition focus:border-emerald-500/40"
            />
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="journey-inbox-refresh"
            className="inline-flex items-center justify-center gap-1.5 rounded border border-white/10 px-3 py-2 text-xs text-zinc-300 transition hover:border-white/20 hover:bg-white/5"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Aktualisieren
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <FilterSelect
            testId="journey-inbox-filter-kind"
            value={kind}
            onChange={setKind}
            options={KIND_OPTIONS}
            label="Leadtyp"
          />
          <FilterSelect
            testId="journey-inbox-filter-stage"
            value={pipelineStage}
            onChange={setPipelineStage}
            options={STAGE_OPTIONS}
            label="Status"
          />
          <FilterSelect
            testId="journey-inbox-filter-source"
            value={sourceType}
            onChange={setSourceType}
            options={SOURCE_OPTIONS}
            label="Quelle"
          />
        </div>

        <FilterChips
          search={search}
          kind={kind}
          pipelineStage={pipelineStage}
          sourceType={sourceType}
          needsNextStep={needsNextStep}
          onClearSearch={() => setSearchInput("")}
          onClearKind={() => setKind("")}
          onClearStage={() => setPipelineStage("")}
          onClearSource={() => setSourceType("")}
          onClearNeedsNextStep={() => setNeedsNextStep(false)}
          onClearAll={() => {
            setSearchInput("");
            setKind("");
            setPipelineStage("");
            setSourceType("");
            setNeedsNextStep(false);
          }}
        />

        <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.04] pt-3">
          <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Quick</span>
          <button
            type="button"
            onClick={() => setNeedsNextStep((v) => !v)}
            data-testid="journey-inbox-quick-no-next-step"
            className={[
              "inline-flex items-center gap-1.5 rounded border px-2 py-1 text-[11px] transition",
              needsNextStep
                ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                : "border-white/10 text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-200",
            ].join(" ")}
          >
            Ohne nächsten Schritt
          </button>
        </div>
      </section>

      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState
          testId="journey-inbox-empty"
          icon={InboxIcon}
          title={filtersActive ? "Keine Leads für diese Filter" : "Noch keine Leads"}
          description={
            filtersActive
              ? "Setze die Filter zurück oder ändere deinen Suchbegriff."
              : "Sobald Leads über die Website eingehen, erscheinen sie hier."
          }
        />
      ) : (
        <>
          <div
            data-testid="journey-inbox-table"
            className="hidden overflow-hidden rounded-md border border-white/[0.06] lg:block"
          >
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[90px]" />
                <col className="w-[200px]" />
                <col className="w-[140px]" />
                <col className="w-[120px]" />
                <col className="w-[110px]" />
                <col className="w-[110px]" />
                <col className="w-[120px]" />
                <col className="w-[140px]" />
                <col className="w-[120px]" />
                <col className="w-[80px]" />
                <col className="w-[110px]" />
              </colgroup>
              <thead className="bg-white/[0.02] text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Eingang</th>
                  <th className="px-4 py-3 font-medium">Lead</th>
                  <th className="px-4 py-3 font-medium">Unternehmen</th>
                  <th className="px-4 py-3 font-medium">Typ</th>
                  <th className="px-4 py-3 font-medium">Quelle</th>
                  <th className="px-4 py-3 font-medium">Region</th>
                  <th className="px-4 py-3 font-medium">System</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Nächster Schritt</th>
                  <th className="px-4 py-3 font-medium">Mail</th>
                  <th className="px-4 py-3 text-right font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <LeadRow key={it.id} item={it} />
                ))}
              </tbody>
            </table>
          </div>

          <div
            data-testid="journey-inbox-cards"
            className="grid gap-3 lg:hidden"
          >
            {items.map((it) => (
              <LeadCard key={it.id} item={it} />
            ))}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}

function Chip({ label, value, onClear, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-200"
    >
      <span className="text-emerald-400/70">{label}:</span>
      <span className="font-medium">{value}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label="Filter entfernen"
        className="ml-0.5 rounded p-0.5 transition hover:bg-emerald-500/20"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function FilterChips({
  search, kind, pipelineStage, sourceType, needsNextStep,
  onClearSearch, onClearKind, onClearStage, onClearSource, onClearNeedsNextStep, onClearAll,
}) {
  const items = [];
  if (search) items.push({ label: "Suche", value: search, onClear: onClearSearch, testId: "journey-chip-search" });
  if (kind) {
    const opt = KIND_OPTIONS.find((o) => o.value === kind);
    items.push({ label: "Typ", value: opt?.label || kind, onClear: onClearKind, testId: "journey-chip-kind" });
  }
  if (pipelineStage) {
    const opt = STAGE_OPTIONS.find((o) => o.value === pipelineStage);
    items.push({ label: "Status", value: opt?.label || pipelineStage, onClear: onClearStage, testId: "journey-chip-stage" });
  }
  if (sourceType) {
    const opt = SOURCE_OPTIONS.find((o) => o.value === sourceType);
    items.push({ label: "Quelle", value: opt?.label || sourceType, onClear: onClearSource, testId: "journey-chip-source" });
  }
  if (needsNextStep) {
    items.push({ label: "Quick", value: "Ohne nächsten Schritt", onClear: onClearNeedsNextStep, testId: "journey-chip-needs-next-step" });
  }
  if (items.length === 0) return null;

  return (
    <div
      data-testid="journey-inbox-chips"
      className="flex flex-wrap items-center gap-2 border-t border-white/[0.04] pt-3"
    >
      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
        Aktive Filter
      </span>
      {items.map((c) => (
        <Chip key={c.testId} {...c} />
      ))}
      <button
        type="button"
        onClick={onClearAll}
        data-testid="journey-inbox-clear-all"
        className="text-[11px] text-zinc-400 underline-offset-2 transition hover:text-zinc-200 hover:underline"
      >
        Alle Filter zurücksetzen
      </button>
    </div>
  );
}

function FilterSelect({ value, onChange, options, label, testId }) {
  return (
    <div className="flex flex-1 items-center gap-2">
      <Filter className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        aria-label={label}
        className="w-full rounded border border-white/10 bg-[#0B0F10] px-2 py-2 text-xs text-zinc-200 outline-none transition focus:border-emerald-500/40"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function LoadingState() {
  return (
    <div
      data-testid="journey-inbox-loading"
      className="space-y-2 rounded-md border border-white/[0.06] p-4"
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="h-10 animate-pulse rounded bg-white/[0.03]"
        />
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div
      data-testid="journey-inbox-error"
      className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-5 text-sm text-red-200"
    >
      <div className="mb-2 flex items-center gap-2 font-medium">
        <AlertCircle className="h-4 w-4" />
        Fehler beim Laden der Leads
      </div>
      <div className="mb-3 text-xs text-red-200/80">{message}</div>
      <button
        type="button"
        onClick={onRetry}
        data-testid="journey-inbox-retry"
        className="rounded border border-red-400/30 px-3 py-1.5 text-xs text-red-100 transition hover:bg-red-500/10"
      >
        Erneut versuchen
      </button>
    </div>
  );
}

function Pagination({ page, totalPages, total, onChange }) {
  if (totalPages <= 1) {
    return (
      <div className="text-right text-xs text-zinc-500">
        {total} Ergebnis{total === 1 ? "" : "se"}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between text-xs text-zinc-400">
      <div>
        {total} Ergebnisse — Seite {page} von {totalPages}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          data-testid="journey-inbox-prev"
          className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-1 text-zinc-300 transition hover:border-white/20 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Zurück
        </button>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          data-testid="journey-inbox-next"
          className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-1 text-zinc-300 transition hover:border-white/20 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Weiter
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
